import React, { useEffect, useState } from "react";
import { Video, Plus, Copy, ExternalLink, Loader2, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

// Group video calling / conferencing / presentation — powered by Jitsi Meet
// (open source, no key). Backend just mints and stores the room key.
export default function NearMeet() {
  const { user } = useAuth();
  const [rooms, setRooms] = useState([]);
  const [creating, setCreating] = useState(false);
  const [active, setActive] = useState(null);         // room being joined (iframe)
  const [f, setF] = useState({ name:"", is_private:true, schedule_at:"", invitees:"" });

  useSeo({ title:"Near Meet · Group calls · Conferences · Presentations · find2near",
           description:"Free, encrypted group calls & conferences with screen sharing and recording. No app install required.", canonical:"/meet" });

  const load = () => user && api.get("/meet/rooms").then(r=>setRooms(r.data)).catch(()=>{});
  useEffect(() => { load(); }, [user]);   // wrap so useEffect never sees the Promise return

  const create = async () => {
    if (!f.name.trim()) { toast.error("Room name required"); return; }
    try {
      const r = await api.post("/meet/rooms", { ...f, invitees: f.invitees.split(/[,\s]+/).filter(Boolean) });
      toast.success("Room ready"); setCreating(false); setF({ name:"", is_private:true, schedule_at:"", invitees:"" });
      setActive(r.data); load();
    } catch (e) { toast.error(e?.response?.data?.detail||"Failed"); }
  };

  const copyUrl = (url) => { navigator.clipboard.writeText(url); toast.success("URL copied"); };

  if (active) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col" style={{ zIndex: "var(--z-modal)" }} data-testid="meet-active">
        <div className="flex items-center justify-between bg-stone-900 text-white px-4 py-2">
          <div className="text-sm">
            <b>{active.name}</b> · <span className="opacity-70 text-xs font-mono">{active.room_key}</span>
          </div>
          <div className="flex gap-2">
            <button onClick={()=>copyUrl(active.url)} className="text-xs bg-white/10 rounded-full px-3 py-1 inline-flex items-center gap-1"><Copy size={11}/> Copy invite</button>
            <button onClick={()=>setActive(null)} className="text-xs bg-red-600 rounded-full px-3 py-1"><X size={11} className="inline mr-1"/> Leave</button>
          </div>
        </div>
        <iframe title="meet" src={`${active.url}#config.prejoinPageEnabled=false&userInfo.displayName=%22${encodeURIComponent(user?.name || "Guest")}%22`}
                allow="camera; microphone; fullscreen; display-capture; autoplay; clipboard-write"
                className="flex-1 w-full border-0" data-testid="meet-iframe"/>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-6" data-testid="meet-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Video size={12} className="text-indigo-600"/> Near Meet · Group calls</div>
          <h1 className="heading text-3xl md:text-4xl font-black text-stone-900">Meet, present, <span className="text-indigo-600">record.</span></h1>
          <p className="text-stone-500 text-sm mt-1">Encrypted group calls, screen-sharing and lightweight recording — powered by Jitsi Meet. No app install.</p>
        </div>
        {user && <Button onClick={()=>setCreating(true)} className="rounded-full bg-indigo-600 hover:bg-indigo-700 font-black" data-testid="meet-new"><Plus size={14} className="mr-1.5"/> New room</Button>}
      </div>
      {creating && (
        <div className="fixed inset-0 z-[110] bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl">
            <div className="px-5 py-3 border-b border-stone-200 flex items-center justify-between"><b>New meeting room</b><button onClick={()=>setCreating(false)}><X size={18} className="text-stone-500"/></button></div>
            <div className="p-5 space-y-2">
              <Input value={f.name} onChange={e=>setF({...f,name:e.target.value})} placeholder="Room name (e.g. Weekly stand-up)" className="rounded-2xl h-10"/>
              <Input value={f.schedule_at} onChange={e=>setF({...f,schedule_at:e.target.value})} type="datetime-local" className="rounded-2xl h-10 font-mono"/>
              <Input value={f.invitees} onChange={e=>setF({...f,invitees:e.target.value})} placeholder="Invitees — NVN / mobile / email (comma-separated)" className="rounded-2xl h-10 text-xs"/>
              <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={f.is_private} onChange={e=>setF({...f,is_private:e.target.checked})}/> Private (invite-only)</label>
              <Button onClick={create} className="w-full rounded-full bg-indigo-600 hover:bg-indigo-700 font-black">Create & join</Button>
            </div>
          </div>
        </div>
      )}
      {!user
        ? <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">Sign in to create or join rooms.</div>
        : rooms.length===0
          ? <div className="text-center py-16 text-stone-400 border border-dashed border-stone-200 rounded-3xl">No rooms yet — create one!</div>
          : <div className="grid gap-2">
              {rooms.map(r=>(
                <div key={r.room_id} className="rounded-3xl bg-white border border-stone-200 p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-2xl bg-indigo-50 text-indigo-700 grid place-items-center"><Users size={16}/></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-stone-900">{r.name}</div>
                    <div className="text-[11px] font-mono text-stone-500">{r.room_key} · {r.schedule_at ? new Date(r.schedule_at).toLocaleString() : "instant"}</div>
                  </div>
                  <button onClick={()=>copyUrl(r.url)} className="text-xs font-bold text-stone-500 hover:text-stone-900" data-testid={`meet-copy-${r.room_id}`}><Copy size={13}/></button>
                  <Button onClick={()=>setActive(r)} className="rounded-full bg-indigo-600 hover:bg-indigo-700 h-9" data-testid={`meet-join-${r.room_id}`}>Join</Button>
                </div>
              ))}
            </div>}
    </div>
  );
}
